import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MagnifyingGlass, Eye, EyeSlash, Link, QrCode, PencilSimple, Trash, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIPO_STYLE = {
  standard: "bg-emerald-100 text-emerald-800 border-emerald-300",
  evento: "bg-amber-100 text-amber-800 border-amber-300",
  coda: "bg-sky-100 text-sky-800 border-sky-300",
};

export default function ModuliWeb() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [forms, setForms] = useState([]);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("");
  const [loading, setLoading] = useState(true);
  const [statsFor, setStatsFor] = useState(null);
  const rid = user?.restaurant_id;

  const load = useCallback(() => {
    setLoading(true);
    api.get("/web-forms").then((r) => setForms(r.data)).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const publicUrl = (f) => `${window.location.origin}/p/${rid}/m/${f.slug}`;

  const copyLink = async (f) => {
    try {
      await navigator.clipboard.writeText(publicUrl(f));
      toast.success("Link del modulo copiato");
    } catch { toast.error("Copia non riuscita"); }
  };

  const downloadQr = async (f) => {
    try {
      const r = await api.get("/tools/qr", { params: { content: publicUrl(f) }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `qr-${f.slug}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = async (f) => {
    try {
      const r = await api.post(`/web-forms/${f.id}/toggle`);
      toast.success(r.data.active ? "Modulo attivato" : "Modulo disattivato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Eliminare il modulo "${f.name}"? Le prenotazioni esistenti restano nel gestionale.`)) return;
    try {
      await api.delete(`/web-forms/${f.id}`);
      toast.success("Modulo eliminato");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = forms.filter((f) =>
    (!stato || (stato === "attivi" ? f.active : !f.active)) &&
    (!q || f.name.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-4" data-testid="moduli-web-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Moduli Web</h1>
          <p className="text-xs text-zinc-500">Moduli pubblici per eventi, degustazioni, coupon e iniziative prenotabili</p>
        </div>
        {canEdit && (
          <button onClick={() => navigate("/moduli/nuovo")} data-testid="btn-nuovo-modulo"
            className="h-11 px-4 rounded-md bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2 hover:bg-zinc-800 active:scale-95 transition">
            <Plus size={16} weight="bold" /> Modulo
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca modulo..." data-testid="moduli-search"
            className="w-full h-11 pl-9 pr-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
        </div>
        <div className="flex gap-1.5" data-testid="moduli-filter-stato">
          {[["", "Tutti"], ["attivi", "Attivi"], ["non_attivi", "Non attivi"]].map(([k, l]) => (
            <button key={k} onClick={() => setStato(k)} data-testid={`moduli-filtro-${k || "tutti"}`}
              className={`h-11 px-3 rounded-md text-xs font-semibold border transition ${stato === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2" data-testid="moduli-list">
        {loading && <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-zinc-200 rounded-lg py-10 text-center text-sm text-zinc-400" data-testid="moduli-empty">
            Nessun modulo. Creane uno per raccogliere prenotazioni da eventi, coupon e iniziative.
          </div>
        )}
        {filtered.map((f) => (
          <div key={f.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`modulo-${f.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase ${TIPO_STYLE[f.tipo] || TIPO_STYLE.standard}`}>
                {f.tipo}
              </span>
              <span className="font-display font-bold text-zinc-900">{f.name}</span>
              {f.etichetta && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200"
                  data-testid={`modulo-etichetta-${f.id}`}>Etichetta: {f.etichetta}</span>
              )}
              {f.tag_name && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200"
                  data-testid={`modulo-tag-${f.id}`}>Tag: {f.tag_name}</span>
              )}
              <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border ${f.active ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-zinc-100 text-zinc-500 border-zinc-300"}`}
                data-testid={`modulo-stato-${f.id}`}>
                {f.active ? "ATTIVO" : "NON ATTIVO"}
              </span>
            </div>
            <div className="text-xs text-zinc-500 mt-1.5 flex items-center gap-3 flex-wrap">
              <span className="font-mono truncate">/p/{rid}/m/{f.slug}</span>
            </div>
            <div className="flex gap-1.5 mt-3 flex-wrap items-center">
              <button onClick={() => toggle(f)} title={f.active ? "Modulo attivo — clicca per disattivare" : "Modulo non attivo — clicca per attivare"}
                data-testid={`modulo-toggle-${f.id}`}
                className={`h-9 w-9 rounded-md flex items-center justify-center border transition ${f.active ? "bg-white border-zinc-300 text-zinc-600 hover:border-rose-300 hover:text-rose-600" : "bg-emerald-600 border-emerald-600 text-white"}`}>
                {f.active ? <Eye size={15} /> : <EyeSlash size={15} />}
              </button>
              <button onClick={() => copyLink(f)} title="Copia link pubblico" data-testid={`modulo-copy-${f.id}`}
                className="h-9 w-9 rounded-md bg-white border border-zinc-300 text-zinc-600 flex items-center justify-center hover:border-zinc-400">
                <Link size={15} />
              </button>
              <button onClick={() => downloadQr(f)} title="Scarica QR code" data-testid={`modulo-qr-${f.id}`}
                className="h-9 w-9 rounded-md bg-white border border-zinc-300 text-zinc-600 flex items-center justify-center hover:border-zinc-400">
                <QrCode size={15} />
              </button>
              <button onClick={() => setStatsFor(f)} title="Statistiche prenotazioni" data-testid={`modulo-stats-btn-${f.id}`}
                className="h-9 px-2.5 rounded-md bg-white border border-zinc-300 text-zinc-600 text-xs font-semibold flex items-center gap-1 hover:border-zinc-400">
                <ChartBar size={15} /> {f.reservations_count || 0}
              </button>
              <button onClick={() => navigate(`/moduli/${f.id}?tab=orari`)} data-testid={`modulo-orari-${f.id}`}
                className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 hover:border-zinc-400">
                Orari
              </button>
              <button onClick={() => navigate(`/moduli/${f.id}?tab=generali`)} data-testid={`modulo-info-${f.id}`}
                className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 hover:border-zinc-400">
                Info aggiuntive
              </button>
              {canEdit && (
                <>
                  <button onClick={() => navigate(`/moduli/${f.id}`)} data-testid={`modulo-edit-${f.id}`}
                    className="h-9 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
                    <PencilSimple size={13} weight="bold" /> Modifica
                  </button>
                  <button onClick={() => remove(f)} data-testid={`modulo-delete-${f.id}`}
                    className="h-9 w-9 rounded-md bg-white border border-rose-200 text-rose-600 flex items-center justify-center">
                    <Trash size={13} />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {statsFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setStatsFor(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-xs text-center" onClick={(e) => e.stopPropagation()}
            data-testid="modulo-stats-dialog">
            <h3 className="font-display font-bold text-zinc-900">Statistiche — {statsFor.name}</h3>
            <div className="mt-3">
              <div className="font-display text-4xl font-bold text-zinc-900" data-testid="modulo-stats-total">
                {statsFor.reservations_count || 0}
              </div>
              <div className="text-xs text-zinc-500 mt-1">prenotazioni ricevute tramite questo modulo</div>
            </div>
            <p className="text-[11px] text-zinc-400 mt-3">Le prenotazioni sono visibili nella sezione Prenotazioni con il badge dell'etichetta.</p>
            <button onClick={() => setStatsFor(null)} data-testid="modulo-stats-close"
              className="mt-4 w-full h-10 rounded-md bg-zinc-900 text-white text-sm font-semibold">Chiudi</button>
          </div>
        </div>
      )}
    </div>
  );
}
